/**
 * Turn an auth failure into copy that's safe to show an end user.
 *
 * MSAL is explicit that its exceptions are "intended for app developers to
 * troubleshoot, not for displaying to end users" and that the messages are not
 * localized (see learn.microsoft.com/entra/identity-platform/msal-error-handling-js).
 * So we must never render an MSAL error's raw `.message` — that's how strings
 * like "hash_empty_error: Hash value cannot be processed because it is empty…"
 * leaked onto the login screen. Instead we key off the stable `errorCode` and
 * map the handful of codes a real user can actually hit to friendly copy;
 * anything unrecognised collapses to a generic retry message.
 *
 * Non-MSAL errors (notably our own B2C ROPC client, which already throws
 * human-friendly text — see b2cClient.ts) pass their message through unchanged.
 */

const GENERIC_FALLBACK = "Sign in failed. Please try again.";

/**
 * Friendly copy per MSAL `errorCode`. Codes grouped by the user-visible
 * situation they represent. Kept intentionally small — only codes an end user
 * can realistically trigger. See the MSAL browser error reference:
 * learn.microsoft.com/entra/msal/javascript/browser/errors
 */
const MSAL_FRIENDLY: Record<string, string> = {
  // User closed the sign-in window / declined.
  user_cancelled: "Sign-in was cancelled. Please try again when you're ready.",

  // The redirect came back but the response hash was gone by the time MSAL
  // read it — typically a router stripping it, and most common on mobile.
  // From the user's side it's just "try again".
  hash_empty_error: "We couldn't complete sign-in. Please try again.",
  no_token_request_cache_error: "We couldn't complete sign-in. Please try again.",

  // A previous sign-in attempt is still mid-flight.
  interaction_in_progress:
    "A sign-in is already in progress. Finish it in the other window, or wait a moment and try again.",

  // Pop-up blocked or couldn't open / was closed before it loaded.
  popup_window_error:
    "We couldn't open the sign-in pop-up. Allow pop-ups for this site, then try again.",
  empty_window_error:
    "We couldn't open the sign-in pop-up. Allow pop-ups for this site, then try again.",
  popup_window_timeout:
    "The sign-in pop-up took too long to load. Please try again.",
  monitor_window_timeout:
    "Sign-in timed out before it finished. Please try again.",

  // Network / server reachability.
  network_error:
    "We couldn't reach the sign-in service. Check your connection and try again.",
  post_request_failed:
    "We couldn't reach the sign-in service. Check your connection and try again.",

  // Server declined the sign-in.
  access_denied:
    "Access was denied. Contact your administrator if this keeps happening.",
};

/** Pull MSAL's stable `errorCode` off an unknown thrown value, if present. */
function msalErrorCode(err: unknown): string | null {
  if (
    typeof err === "object" &&
    err !== null &&
    "errorCode" in err &&
    typeof (err as { errorCode?: unknown }).errorCode === "string" &&
    (err as { errorCode: string }).errorCode.length > 0
  ) {
    return (err as { errorCode: string }).errorCode;
  }
  return null;
}

/**
 * @param err      Anything thrown by a login path (MSAL, our B2C client, …).
 * @param fallback Copy to show when we can't do better than "it failed".
 */
export function friendlyAuthError(
  err: unknown,
  fallback: string = GENERIC_FALLBACK,
): string {
  // MSAL error: map by code, and crucially never fall back to its raw,
  // untranslated `.message`.
  const code = msalErrorCode(err);
  if (code) return MSAL_FRIENDLY[code] ?? fallback;

  // Non-MSAL Error: our B2C client already throws friendly, end-user-ready
  // copy, so surface it as-is.
  if (err instanceof Error && err.message) return err.message;
  if (typeof err === "string" && err) return err;
  return fallback;
}
